// src/pages/DailyRecap.jsx
// Daily Recap page showing performance analytics with trends and AI insights

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Mail,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Send,
  Activity,
  Heart,
  Pill,
  Users,
  Clock,
  Sparkles,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import api from '../api';

const BASE = process.env.REACT_APP_API_URL || '';

// Automation display names
const AUTOMATION_NAMES = {
  birthday: 'Birthday Greeting',
  policy_anniversary: 'Policy Anniversary',
  welcome_day_1: 'Welcome (Day 1)',
  welcome_day_30: 'Welcome (Day 30)',
  welcome_day_90: 'Welcome (Day 90)',
  oep_jan2: 'OEP January',
  oep_feb1: 'OEP February',
  oep_mar1: 'OEP March',
  phone_blast: 'Phone Blast',
  confidence_pulse: 'Confidence Pulse'
};

// Risk tier colors
const RISK_COLORS = {
  stable: '#22c55e',
  low: '#84cc16',
  moderate: '#eab308',
  elevated: '#f97316',
  high: '#ef4444',
  critical: '#dc2626',
  severe: '#991b1b'
};

// Helper to format date for display
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Helper to format short date
function formatShortDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, change, changeType, subtitle, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
          <Icon size={20} className="sm:hidden" />
          <Icon size={24} className="hidden sm:block" />
        </div>
        {change !== undefined && change !== null && (
          <div className={`flex items-center gap-1 text-sm font-medium ${
            changeType === 'positive' ? 'text-green-600' :
            changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {changeType === 'positive' ? <TrendingUp size={14} /> :
             changeType === 'negative' ? <TrendingDown size={14} /> : null}
            {change > 0 ? '+' : ''}{change}
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="text-2xl sm:text-3xl font-bold text-[#172A3A]">{value}</div>
        <div className="text-gray-600 text-sm">{label}</div>
        {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
      </div>
    </div>
  );
}

// Automation Progress Bar
function AutomationBar({ name, sent, delivered, failed, replies }) {
  const total = sent || 1;
  const deliveryRate = Math.round((delivered / total) * 100);
  const replyRate = replies ? Math.round((replies / total) * 100) : 0;

  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-[#172A3A]">{name}</span>
        <span className="text-sm text-gray-600">
          {delivered}/{sent} delivered ({deliveryRate}%)
          {replies > 0 && <span className="text-green-600 ml-2">• {replies} replies</span>}
        </span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
        <div
          className="bg-green-500 h-full transition-all"
          style={{ width: `${(delivered / total) * 100}%` }}
        />
        <div
          className="bg-red-400 h-full transition-all"
          style={{ width: `${(failed / total) * 100}%` }}
        />
      </div>
    </div>
  );
}

// AI Insights Panel
function AIInsightsPanel({ insights, loading }) {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-purple-600" size={24} />
          <h3 className="font-bold text-[#172A3A] text-lg">AI Insights</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-purple-100 rounded w-full" />
          <div className="h-4 bg-purple-100 rounded w-5/6" />
          <div className="h-4 bg-purple-100 rounded w-4/6" />
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-purple-600" size={24} />
          <h3 className="font-bold text-[#172A3A] text-lg">AI Insights</h3>
        </div>
        <p className="text-gray-600">No insights available for this recap.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <Sparkles className="text-purple-600" size={24} />
        <h3 className="font-bold text-[#172A3A] text-lg">AI Insights</h3>
      </div>
      <p className="text-gray-700 whitespace-pre-line leading-relaxed">{insights}</p>
    </div>
  );
}

export default function DailyRecap() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [recap, setRecap] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [periodType, setPeriodType] = useState('daily'); // daily, weekly, monthly

  // Get date from URL or use yesterday
  const getInitialDate = () => {
    const urlDate = searchParams.get('date');
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate)) {
      return urlDate;
    }
    // Default to yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate);

  // Fetch recap data
  useEffect(() => {
    fetchRecap();
    fetchTrendData();
  }, [selectedDate, periodType]);

  async function fetchRecap() {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${BASE}/daily-recap/${selectedDate}?type=${periodType}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        if (res.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch recap');
      }

      const data = await res.json();
      setRecap(data.data);
    } catch (err) {
      console.error('Failed to fetch recap:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchTrendData() {
    try {
      setTrendLoading(true);
      const res = await fetch(`${BASE}/daily-recap/trend/data?days=7`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setTrendData(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch trend data:', err);
    } finally {
      setTrendLoading(false);
    }
  }

  async function handleSendEmail() {
    try {
      setSending(true);
      const res = await fetch(`${BASE}/daily-recap/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate })
      });

      if (res.ok) {
        alert('Recap email sent successfully!');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (err) {
      alert('Failed to send email: ' + err.message);
    } finally {
      setSending(false);
    }
  }

  async function handleGenerateRecap() {
    try {
      setLoading(true);
      const res = await fetch(`${BASE}/daily-recap/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate })
      });

      if (res.ok) {
        await fetchRecap();
      } else {
        throw new Error('Failed to generate recap');
      }
    } catch (err) {
      alert('Failed to generate recap: ' + err.message);
    }
  }

  function navigateDate(direction) {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + direction);
    const newDate = current.toISOString().split('T')[0];
    setSelectedDate(newDate);
    setSearchParams({ date: newDate });
  }

  // Calculate delivery rate
  const deliveryRate = recap?.sms_sent > 0
    ? Math.round((recap.sms_delivered / recap.sms_sent) * 100)
    : 100;

  // Parse automation stats (exclude internal keys)
  const rawAutomationStats = recap?.automation_stats || {};
  const upcomingAutomations = rawAutomationStats._upcoming || null;
  const scoreChanges = rawAutomationStats._score_changes || [];
  const automationStats = Object.fromEntries(
    Object.entries(rawAutomationStats).filter(([k]) => !k.startsWith('_'))
  );
  const hasAutomations = Object.keys(automationStats).length > 0;

  // Parse risk distribution
  const riskDistribution = recap?.risk_distribution || {};
  const riskChartData = Object.entries(riskDistribution)
    .filter(([key]) => key !== 'total')
    .map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value: parseInt(value) || 0,
      fill: RISK_COLORS[name] || '#94a3b8'
    }))
    .filter(d => d.value > 0);

  // Parse Blue Button changes
  const bluebuttonChanges = recap?.bluebutton_changes || {};

  // Loading state
  if (loading && !recap) {
    return (
      <div className="min-h-screen bg-gray-100 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="animate-spin text-[#00A9E0]" size={40} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#172A3A]">Daily Recap</h1>
              <p className="text-gray-600 mt-1">
                Your performance summary for {formatDate(selectedDate)}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Navigation */}
              <div className="flex items-center bg-gray-100 rounded-lg">
                <button
                  onClick={() => navigateDate(-1)}
                  className="p-2 hover:bg-gray-200 rounded-l-lg transition"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="px-3 py-2 font-medium">
                  {formatShortDate(selectedDate)}
                </div>
                <button
                  onClick={() => navigateDate(1)}
                  className="p-2 hover:bg-gray-200 rounded-r-lg transition"
                  disabled={selectedDate >= new Date().toISOString().split('T')[0]}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Action Buttons */}
              <button
                onClick={handleSendEmail}
                disabled={sending || !recap}
                className="flex items-center gap-2 px-4 py-2 bg-[#00A9E0] text-white rounded-lg font-semibold hover:bg-[#007CB0] transition disabled:opacity-50"
              >
                <Mail size={18} />
                <span className="hidden sm:inline">{sending ? 'Sending...' : 'Email'}</span>
              </button>
            </div>
          </div>

          {/* Period Toggle */}
          <div className="mt-4 flex gap-2">
            {['daily', 'weekly', 'monthly'].map((type) => (
              <button
                key={type}
                onClick={() => setPeriodType(type)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  periodType === type
                    ? 'bg-[#00A9E0] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* No Data State */}
        {!recap && !loading && (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-xl font-bold text-[#172A3A] mb-2">No Recap Available</h3>
            <p className="text-gray-600 mb-6">
              There's no recap for {formatDate(selectedDate)} yet.
            </p>
            <button
              onClick={handleGenerateRecap}
              className="px-6 py-3 bg-[#00A9E0] text-white rounded-lg font-semibold hover:bg-[#007CB0] transition"
            >
              Generate Recap Now
            </button>
          </div>
        )}

        {/* Main Content - Only show if we have data */}
        {recap && (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={MessageSquare}
                label="SMS Sent"
                value={recap.sms_sent || 0}
                change={null}
                color="blue"
                subtitle={`${recap.sms_delivered || 0} delivered`}
              />
              <StatCard
                icon={Mail}
                label="Emails Sent"
                value={recap.emails_sent || 0}
                change={null}
                color="purple"
              />
              <StatCard
                icon={MessageCircle}
                label="Replies"
                value={recap.sms_inbound || 0}
                change={null}
                color="green"
              />
              <StatCard
                icon={CheckCircle2}
                label="Delivery Rate"
                value={`${deliveryRate}%`}
                change={null}
                changeType={deliveryRate >= 95 ? 'positive' : deliveryRate < 85 ? 'negative' : null}
                color={deliveryRate >= 95 ? 'green' : deliveryRate < 85 ? 'red' : 'blue'}
              />
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Automations */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Activity className="text-blue-600" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Automations</h3>
                </div>

                {hasAutomations ? (
                  <div className="space-y-1">
                    {Object.entries(automationStats).map(([key, data]) => (
                      <AutomationBar
                        key={key}
                        name={data.name || AUTOMATION_NAMES[key] || key}
                        sent={data.sent || data.sms_sent || 0}
                        delivered={data.delivered || data.sent || 0}
                        failed={data.failed || 0}
                        replies={data.replied || data.responded || 0}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="mx-auto text-gray-300 mb-2" size={40} />
                    <p>No automations ran on this day</p>
                  </div>
                )}
              </div>

              {/* Blue Button Health Data */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="text-red-500" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Blue Button Health Data</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <div className="text-2xl font-bold text-blue-600">
                      {recap.bluebutton_syncs || 0}
                    </div>
                    <div className="text-sm text-gray-600">Syncs</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-green-600">
                      {recap.bluebutton_new_claims || 0}
                    </div>
                    <div className="text-sm text-gray-600">New Claims</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <div className="text-2xl font-bold text-purple-600">
                      {Object.values(bluebuttonChanges).reduce((a, b) => a + (parseInt(b) || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">Changes</div>
                  </div>
                </div>

                {Object.keys(bluebuttonChanges).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-700 mb-2">Changes Breakdown</h4>
                    {Object.entries(bluebuttonChanges).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Pill size={14} className="text-purple-500" />
                          {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                )}

                {Object.keys(bluebuttonChanges).length === 0 && (
                  <p className="text-gray-500 text-center">No prescription changes detected</p>
                )}
              </div>
            </div>

            {/* Upcoming Automations & Score Changes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Automations */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="text-purple-600" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Upcoming Automations</h3>
                </div>

                {upcomingAutomations && upcomingAutomations.total > 0 ? (
                  <div>
                    <div className="text-center p-4 bg-purple-50 rounded-xl mb-4">
                      <div className="text-2xl font-bold text-purple-600">{upcomingAutomations.total}</div>
                      <div className="text-sm text-gray-600">Queued for today (1:00 PM ET)</div>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(upcomingAutomations.byType || {}).map(([key, data]) => (
                        <div key={key} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="font-medium text-[#172A3A]">
                            {data.name || AUTOMATION_NAMES[key] || key}
                          </span>
                          <div className="flex items-center gap-3 text-sm">
                            {data.sms > 0 && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <MessageSquare size={14} /> {data.sms}
                              </span>
                            )}
                            {data.email > 0 && (
                              <span className="flex items-center gap-1 text-purple-600">
                                <Mail size={14} /> {data.email}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="mx-auto text-gray-300 mb-2" size={40} />
                    <p>No automations queued for today</p>
                  </div>
                )}
              </div>

              {/* Score Changes */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="text-orange-500" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Top Score Changes</h3>
                </div>

                {scoreChanges.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {scoreChanges.map((mover, idx) => (
                      <div
                        key={mover.clientId || idx}
                        className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 border-b last:border-0 cursor-pointer"
                        onClick={() => navigate(`/clients/${mover.clientId}`)}
                      >
                        <span className="font-medium text-[#172A3A] text-sm truncate mr-3">
                          {mover.name}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-gray-400 text-sm">{mover.previousScore}</span>
                          <span className="text-gray-400">&rarr;</span>
                          <span className="text-sm font-bold">{mover.score}</span>
                          <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                            mover.change > 0
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {mover.change > 0 ? '+' : ''}{mover.change}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="mx-auto text-gray-300 mb-2" size={40} />
                    <p>No significant score changes</p>
                  </div>
                )}
              </div>
            </div>

            {/* Risk & Tasks Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Risk Distribution */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <PieChartIcon className="text-orange-500" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Client Risk Distribution</h3>
                </div>

                {riskChartData.length > 0 ? (
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <div className="w-36 h-36 sm:w-48 sm:h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={riskChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                          >
                            {riskChartData.map((entry, idx) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-2">
                      {riskChartData.map((entry) => (
                        <div key={entry.name} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: entry.fill }}
                            />
                            {entry.name}
                          </span>
                          <span className="font-medium">{entry.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <PieChartIcon className="mx-auto text-gray-300 mb-2" size={40} />
                    <p>No risk data available</p>
                  </div>
                )}

                {/* Risk Movers */}
                <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-red-600">
                      {recap.risk_increases || 0}
                    </div>
                    <div className="text-xs text-gray-500">Risk Increased</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-green-600">
                      {recap.risk_decreases || 0}
                    </div>
                    <div className="text-xs text-gray-500">Risk Decreased</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-orange-600">
                      {recap.auto_100_count || 0}
                    </div>
                    <div className="text-xs text-gray-500">Auto-100</div>
                  </div>
                </div>
              </div>

              {/* Tasks & Productivity */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="text-green-600" size={24} />
                  <h3 className="font-bold text-[#172A3A] text-lg">Tasks & Productivity</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <div className="text-2xl font-bold text-blue-600">
                      {recap.tasks_created || 0}
                    </div>
                    <div className="text-sm text-gray-600">Created</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-green-600">
                      {recap.tasks_completed || 0}
                    </div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                  <div className={`text-center p-4 rounded-xl ${
                    (recap.tasks_overdue || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      (recap.tasks_overdue || 0) > 0 ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {recap.tasks_overdue || 0}
                    </div>
                    <div className="text-sm text-gray-600">Overdue</div>
                  </div>
                </div>

                {/* Clients Contacted */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="text-blue-500" size={20} />
                      <span className="text-gray-700">Clients Contacted</span>
                    </div>
                    <span className="text-xl font-bold text-[#172A3A]">
                      {recap.clients_contacted || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-3">
                      <Users className="text-green-500" size={20} />
                      <span className="text-gray-700">New Clients Added</span>
                    </div>
                    <span className="text-xl font-bold text-[#172A3A]">
                      {recap.clients_added || 0}
                    </span>
                  </div>
                </div>

                {/* Alert for overdue tasks */}
                {(recap.tasks_overdue || 0) > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                    <span className="text-sm text-red-700">
                      You have {recap.tasks_overdue} overdue task{recap.tasks_overdue > 1 ? 's' : ''}.
                      <button
                        onClick={() => navigate('/tasks?filter=overdue')}
                        className="ml-1 underline font-medium"
                      >
                        View tasks
                      </button>
                    </span>
                  </div>
                )}

                {/* Alert for auto-100 clients */}
                {(recap.auto_100_count || 0) > 0 && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
                    <AlertTriangle className="text-orange-500 flex-shrink-0" size={20} />
                    <span className="text-sm text-orange-700">
                      {recap.auto_100_count} client{recap.auto_100_count > 1 ? 's' : ''} at Auto-100 risk need immediate attention.
                      <button
                        onClick={() => navigate('/risk?tier=severe')}
                        className="ml-1 underline font-medium"
                      >
                        View clients
                      </button>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 7-Day Trend Chart */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="text-blue-600" size={24} />
                <h3 className="font-bold text-[#172A3A] text-lg">7-Day Trend</h3>
              </div>

              {trendLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <RefreshCw className="animate-spin text-gray-400" size={32} />
                </div>
              ) : trendData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorSms" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00A9E0" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#00A9E0" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                      <XAxis
                        dataKey="recap_date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => formatShortDate(val)}
                      />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} unit="%" />
                      <Tooltip
                        labelFormatter={(val) => formatShortDate(val)}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="sms_sent"
                        name="SMS Sent"
                        stroke="#00A9E0"
                        strokeWidth={2}
                        fill="url(#colorSms)"
                        yAxisId="left"
                      />
                      <Line
                        type="monotone"
                        dataKey="delivery_rate"
                        name="Delivery Rate %"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        yAxisId="right"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <BarChart3 className="mx-auto text-gray-300 mb-2" size={40} />
                    <p>Not enough data for trend analysis</p>
                    <p className="text-sm">Check back after a few more days</p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Insights */}
            <AIInsightsPanel insights={recap.ai_insights} loading={false} />
          </>
        )}
      </div>
    </div>
  );
}
